/**
 * Linha do tempo vertical do lead: marco zero em cima, eventos agrupados por
 * dia, filtro por categoria (ligacoes, WhatsApp, e-mail, lead e comercial,
 * pagamentos, motor) e o contato creditado marcado.
 *
 * Decisoes:
 *  - O MARCO ZERO (abandono ou vencimento) e um bloco fixo acima da lista, e
 *    nao um item entre os outros: e a origem de tudo, e o filtro nunca o
 *    esconde. Cada item mostra a distancia ate ele ("T+2h49", "D+16") para
 *    ficar visivel que nada aconteceu antes do fato.
 *  - Agrupar por dia (Hoje, Ontem, data por extenso) resolve a leitura de um
 *    lead que passou 90 dias na regua de expirados; o cabecalho do dia gruda
 *    no topo enquanto o grupo rola.
 *  - Os eventos do servidor sao ordenados por `at`; os eventos LOCAIS
 *    (registrados nesta previa) vao num grupo proprio no fim -- o relogio da
 *    previa e `AGORA_SIMULADO` e o do navegador pode estar antes ou depois
 *    dele; intercalar por hora poderia jogar "vendedora fechou a venda" para
 *    antes do "webhook recebido".
 *  - O `AudioPlayerProvider` envolve SO esta lista: e o unico lugar da pagina
 *    com gravacao, e uma ligacao nunca toca em cima da outra (RNF17).
 */

import { useMemo, useState } from 'react';
import { AudioPlayerProvider } from '../../../components/AudioPlayerProvider';
import { Badge, FilterChips, Icon, type ChipOption } from '../../../components/ui';
import { formatBRLFromCents, formatCountCompact, formatDateTime, PROVIDER_LABEL } from '../../../lib/format';
import type { LeadDetail, TimelineEvent } from '../types';
import { CATEGORIA, categoriaDoEvento, chaveDoDia, descreverRelativo, FATO, ORDEM_DAS_CATEGORIAS, rotuloDoDia, type CategoriaDoEvento } from '../vocabulario';
import { ItemDaLinha } from './ItemDaLinha';

const TUDO = 'tudo';

interface GrupoDoDia {
  readonly chave: string;
  readonly rotulo: string;
  readonly eventos: readonly TimelineEvent[];
}

function agruparPorDia(eventos: readonly TimelineEvent[], agora: Date): readonly GrupoDoDia[] {
  const grupos: GrupoDoDia[] = [];
  for (const e of eventos) {
    const chave = chaveDoDia(new Date(e.at));
    const ultimo = grupos[grupos.length - 1];
    if (ultimo !== undefined && ultimo.chave === chave) {
      grupos[grupos.length - 1] = { ...ultimo, eventos: [...ultimo.eventos, e] };
    } else {
      grupos.push({ chave, rotulo: rotuloDoDia(e.at, agora), eventos: [e] });
    }
  }
  return grupos;
}

function eCreditado(event: TimelineEvent, lead: LeadDetail): boolean {
  const credito = lead.conversion === null || lead.conversion.reversed ? null : lead.conversion.creditedTo;
  if (credito === null) {
    return false;
  }
  if (event.kind === 'voice_call') {
    return credito.channel === 'voice' && event.at === credito.at;
  }
  if (event.kind === 'whatsapp') {
    return credito.channel === 'whatsapp' && event.stage === 'sent' && event.at === credito.at;
  }
  if (event.kind === 'email') {
    return credito.channel === 'email' && event.stage === 'sent' && event.at === credito.at;
  }
  return false;
}

export function LinhaDoTempo({ lead, locais, agora }: { readonly lead: LeadDetail; readonly locais: readonly TimelineEvent[]; readonly agora: Date }): JSX.Element {
  const [categoria, setCategoria] = useState<string>(TUDO);
  const fato = FATO[lead.funnel];

  const ordenados = useMemo(() => [...lead.timeline].sort((a, b) => a.at.localeCompare(b.at)), [lead.timeline]);
  const marcoZero = ordenados.find((e) => e.kind === 'webhook_received') ?? null;
  const semMarco = useMemo(() => ordenados.filter((e) => e.kind !== 'webhook_received'), [ordenados]);

  const contagem = useMemo(() => {
    const mapa: Record<CategoriaDoEvento, number> = { voz: 0, whatsapp: 0, email: 0, pessoas: 0, dinheiro: 0, motor: 0 };
    for (const e of semMarco) {
      mapa[categoriaDoEvento(e)] += 1;
    }
    return mapa;
  }, [semMarco]);

  const opcoes: readonly ChipOption<string>[] = [
    { value: TUDO, label: 'Tudo', count: formatCountCompact(semMarco.length) },
    ...ORDEM_DAS_CATEGORIAS.filter((c) => contagem[c] > 0).map((c) => ({ value: c, label: CATEGORIA[c].label, count: formatCountCompact(contagem[c]) })),
  ];

  const filtrados = categoria === TUDO ? semMarco : semMarco.filter((e) => categoriaDoEvento(e) === categoria);
  const grupos = useMemo(() => agruparPorDia(filtrados, agora), [filtrados, agora]);
  const categoriaAtiva = ORDEM_DAS_CATEGORIAS.find((c) => c === categoria);

  return (
    <div className="flex flex-col gap-4">
      {/* Marco zero: a origem de tudo. O filtro nunca o esconde. */}
      <div className="flex items-start gap-3 rounded-xl border border-accent/40 bg-accent/5 px-4 py-3">
        <span aria-hidden="true" className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-accent/40 bg-accent/10 text-accent">
          <Icon name={fato.icone} size="sm" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-subtle">
            <Badge tone="accent">T0 · marco zero</Badge>
            <time dateTime={lead.abandonedAt} className="tabular-nums">
              {formatDateTime(lead.abandonedAt)}
            </time>
            <span>· {descreverRelativo(lead.abandonedAt, agora)}</span>
          </p>
          <p className="mt-0.5 text-sm font-medium text-ink">
            {fato.verbo}
            {marcoZero !== null && marcoZero.kind === 'webhook_received' ? ` · ${PROVIDER_LABEL[marcoZero.provider]} avisou` : ''}
          </p>
          <p className="mt-0.5 text-sm text-ink-muted">
            {lead.funnel === 'expirados' ? `Plano de ${formatBRLFromCents(lead.amountCents)} chegou ao fim sem renovação` : `Checkout de ${formatBRLFromCents(lead.amountCents)} ficou 30 minutos sem pagamento`}
            {marcoZero !== null && marcoZero.kind === 'webhook_received' ? ` · ${lead.funnel === 'expirados' ? 'assinatura' : 'pedido'} ${marcoZero.externalId}` : ''}. Tudo abaixo aconteceu depois deste ponto: a régua só age sobre quem já saiu.
          </p>
        </div>
      </div>

      {semMarco.length > 1 ? <FilterChips label="Filtrar a linha do tempo por tipo de evento" prefix="Mostrar:" options={opcoes} value={categoria} onChange={setCategoria} tone="accent" /> : null}

      <AudioPlayerProvider>
        {grupos.length === 0 && locais.length === 0 ? (
          <p className="rounded-xl border border-dashed border-edge px-4 py-6 text-center text-sm text-ink-subtle">
            {categoriaAtiva === undefined ? 'Ainda não houve nenhum toque: o lead está esperando o primeiro.' : `Nenhum evento de “${CATEGORIA[categoriaAtiva].label}” para este lead ainda.`}
          </p>
        ) : (
          <ol aria-label="Linha do tempo do lead, agrupada por dia" className="flex flex-col gap-2">
            {grupos.map((g) => (
              <li key={g.chave} className="flex flex-col">
                <h3 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-subtle">
                  <Icon name="calendario" size="xs" />
                  {g.rotulo}
                  <span className="font-normal normal-case tracking-normal text-ink-subtle">
                    · {g.eventos.length} {g.eventos.length === 1 ? 'evento' : 'eventos'}
                  </span>
                </h3>
                <ol className="flex flex-col">
                  {g.eventos.map((event) => (
                    <ItemDaLinha key={event.id} event={event} local={false} agora={agora} funnel={lead.funnel} fatoIso={lead.abandonedAt} creditado={eCreditado(event, lead)} />
                  ))}
                </ol>
              </li>
            ))}
            {locais.length > 0 ? (
              <li className="flex flex-col">
                <h3 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-subtle">
                  <Icon name="editar" size="xs" />
                  Registrado nesta prévia
                </h3>
                <ol className="flex flex-col">
                  {locais.map((event) => (
                    <ItemDaLinha key={event.id} event={event} local agora={agora} funnel={lead.funnel} fatoIso={lead.abandonedAt} creditado={false} />
                  ))}
                </ol>
              </li>
            ) : null}
          </ol>
        )}
      </AudioPlayerProvider>
    </div>
  );
}
